import {
  forwardRef,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import * as S from './styles';
import {
  applyMask,
  calculateCursorPosition,
  detectInputMode,
  DEFAULT_FORMAT_CHARS,
} from './hooks/useMaskEngine';

/**
 * @typedef {Object} InputMaskV2Props
 * @property {string} mask - Máscara a ser aplicada (ex: "(99) 9 9999-9999", "999.999.999-99", "AAA-9999")
 * @property {string} [value=''] - Valor controlado do input (pode ser raw ou masked)
 * @property {function(string, string): void} onChange - Callback chamado com (rawValue, maskedValue)
 * @property {Object.<string, RegExp>} [formatChars] - Caracteres de formato customizados
 * @property {string} [maskPlaceholder='_'] - Caractere placeholder da máscara
 * @property {('small'|'normal'|'large')} [size='large'] - Tamanho do input
 * @property {('default'|'rounded'|'square')} [variant='default'] - Variante visual
 * @property {boolean} [invalid=false] - Estado de erro/validação
 * @property {boolean} [disabled=false] - Estado desabilitado
 * @property {string} [placeholder] - Placeholder do input
 * @property {string} [name] - Nome do input
 * @property {boolean} [autoFocus=false] - Auto focus ao montar
 * @property {string} [autoComplete='off'] - Atributo autocomplete
 * @property {string} [className] - Classes CSS adicionais
 * @property {string} [primaryColor] - Cor primária customizada para borda e highlight (hex, rgb, etc)
 */

/**
 * InputMaskV2 - Componente de input com máscara customizada.
 *
 * Implementação 100% customizada sem dependências externas de máscara,
 * com controle preciso de cursor otimizado para mobile.
 *
 * @component
 * @example
 * // Telefone BR
 * <InputMaskV2
 *   mask="(99) 9 9999-9999"
 *   value={phone}
 *   onChange={(raw, masked) => setPhone({ raw, masked })}
 *   placeholder="+55 (00) 0 0000-0000"
 * />
 *
 * @example
 * // CPF
 * <InputMaskV2
 *   mask="999.999.999-99"
 *   value={cpf}
 *   onChange={(raw, masked) => setCpf(masked)}
 * />
 *
 * @example
 * // Placa de veículo (letras e números)
 * <InputMaskV2
 *   mask="AAA-9*99"
 *   value={placa}
 *   onChange={(raw, masked) => setPlaca(masked)}
 * />
 *
 * @example
 * // Cor hexadecimal
 * <InputMaskV2
 *   mask="#######"
 *   value={hexColor}
 *   onChange={(raw, masked) => setHexColor(masked)}
 *   formatChars={{ '#': /[0-9a-fA-F]/ }}
 * />
 *
 * @param {InputMaskV2Props} props - Props do componente
 * @param {React.Ref} ref - Ref para o elemento input
 * @returns {React.ReactElement}
 */
const InputMaskV2 = forwardRef(
  (
    {
      mask,
      value = '',
      onChange,
      formatChars = DEFAULT_FORMAT_CHARS,
      size = 'large',
      variant = 'default',
      invalid = false,
      disabled = false,
      placeholder,
      name,
      autoFocus = false,
      autoComplete = 'off',
      className,
      primaryColor,
      ...rest
    },
    ref,
  ) => {
    const [cursorPos, setCursorPos] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const inputRef = useRef(null);
    const prevMaskedRef = useRef('');
    const cursorBeforeChangeRef = useRef(0);

    const setRefs = useCallback(
      node => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && typeof ref === 'object') {
          // eslint-disable-next-line no-param-reassign
          ref.current = node;
        }
      },
      [ref],
    );

    // Aplica máscara ao valor atual
    const { masked: maskedValue } = useMemo(() => {
      return applyMask(value, mask, formatChars);
    }, [value, mask, formatChars]);

    // Detecta inputMode automaticamente
    const inputMode = useMemo(() => {
      return detectInputMode(mask, formatChars);
    }, [mask, formatChars]);

    /**
     * Handler de mudança do input.
     * Aplica a máscara, calcula nova posição do cursor e chama onChange.
     */
    const handleChange = useCallback(
      e => {
        const inputElement = e.target;
        const inputValue = inputElement.value;

        const prevCursorPos = cursorBeforeChangeRef.current;

        const { masked: newMasked, raw: newRaw } = applyMask(
          inputValue,
          mask,
          formatChars,
        );

        const newCursorPos = calculateCursorPosition(
          prevCursorPos,
          prevMaskedRef.current,
          newMasked,
          mask,
          formatChars,
          isDeleting,
        );

        prevMaskedRef.current = newMasked;

        setCursorPos(newCursorPos);

        if (onChange) {
          onChange(newRaw, newMasked);
        }
      },
      [mask, formatChars, isDeleting, onChange],
    );

    /**
     * Handler de keydown para detectar deleção (backspace/delete).
     * Também salva a posição do cursor antes da mudança.
     */
    const handleKeyDown = useCallback(e => {
      const isDeletingKey = e.key === 'Backspace' || e.key === 'Delete';
      setIsDeleting(isDeletingKey);

      cursorBeforeChangeRef.current = e.target.selectionStart || 0;
    }, []);

    /**
     * Handler de keyup para resetar flag de deleção.
     */
    const handleKeyUp = useCallback(() => {
      setIsDeleting(false);
    }, []);

    /**
     * Handler de beforeinput para capturar posição antes de input via toque/mobile.
     */
    const handleBeforeInput = useCallback(e => {
      cursorBeforeChangeRef.current = e.target.selectionStart || 0;
    }, []);

    /**
     * Handler de focus para garantir cursor visível.
     * Resolve problema de cursor não aparecer no início em campo vazio.
     */
    const handleFocus = useCallback(e => {
      const input = e.target;

      // Usa setTimeout para garantir que o focus foi aplicado
      // antes de manipular a seleção (importante para mobile)
      setTimeout(() => {
        if (input && document.activeElement === input) {
          const len = input.value?.length || 0;
          try {
            input.setSelectionRange(len, len);
          } catch {
            // Ignora erros
          }
        }
      }, 0);
    }, []);

    /**
     * Efeito para posicionar o cursor após cada render.
     * Usa requestAnimationFrame para garantir timing correto em mobile.
     */
    useLayoutEffect(() => {
      if (cursorPos !== null && inputRef.current) {
        const input = inputRef.current;

        requestAnimationFrame(() => {
          if (
            input &&
            document.activeElement === input &&
            typeof input.setSelectionRange === 'function'
          ) {
            try {
              input.setSelectionRange(cursorPos, cursorPos);
            } catch {
              // Ignora erros em inputs que não suportam setSelectionRange
            }
          }
        });
      }
    }, [cursorPos, maskedValue]);

    /**
     * Efeito para sincronizar referência do valor mascarado anterior.
     */
    useLayoutEffect(() => {
      prevMaskedRef.current = maskedValue;
    }, [maskedValue]);

    return (
      <S.StyledInput
        ref={setRefs}
        inputMode={inputMode}
        value={maskedValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onBeforeInput={handleBeforeInput}
        onFocus={handleFocus}
        $size={size}
        $variant={variant}
        $invalid={invalid}
        $primaryColor={primaryColor}
        disabled={disabled}
        placeholder={placeholder}
        name={name}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        className={className}
        aria-invalid={invalid}
        {...rest}
      />
    );
  },
);

InputMaskV2.displayName = 'InputMaskV2';

InputMaskV2.propTypes = {
  /**
   * Máscara a ser aplicada.
   * Use caracteres especiais: 9 (dígito), A (letra), * (alfanumérico), # (hex)
   * @example "(99) 9 9999-9999" // Telefone BR
   * @example "999.999.999-99" // CPF
   * @example "AAA-9*99" // Placa
   */
  mask: PropTypes.string.isRequired,

  /**
   * Valor controlado do input.
   */
  value: PropTypes.string,

  /**
   * Callback chamado quando o valor muda.
   * Recebe: (rawValue, maskedValue)
   * @param {string} rawValue - Valor sem máscara (apenas caracteres válidos)
   * @param {string} maskedValue - Valor com máscara aplicada
   */
  onChange: PropTypes.func.isRequired,

  /**
   * Caracteres de formato customizados.
   * @default { '9': /\d/, 'A': /[a-zA-Z]/, '*': /[a-zA-Z0-9]/, '#': /[0-9a-fA-F]/, 'S': /[a-zA-Z\s]/ }
   */
  formatChars: PropTypes.objectOf(PropTypes.instanceOf(RegExp)),

  /**
   * Tamanho do input.
   */
  size: PropTypes.oneOf(['small', 'normal', 'large']),

  /**
   * Variante visual do input.
   */
  variant: PropTypes.oneOf(['default', 'rounded', 'square']),

  /**
   * Estado de erro/validação.
   */
  invalid: PropTypes.bool,

  /**
   * Estado desabilitado.
   */
  disabled: PropTypes.bool,

  /**
   * Placeholder do input.
   */
  placeholder: PropTypes.string,

  /**
   * Nome do input (para forms).
   */
  name: PropTypes.string,

  /**
   * Auto focus ao montar o componente.
   */
  autoFocus: PropTypes.bool,

  /**
   * Atributo autocomplete do input.
   */
  autoComplete: PropTypes.string,

  /**
   * Classes CSS adicionais.
   */
  className: PropTypes.string,

  /**
   * Cor primária customizada para borda e highlight no focus.
   * Aceita qualquer valor CSS válido (hex, rgb, rgba, variáveis CSS, etc).
   * @example "#374AA0" // Azul Porter
   * @example "rgb(55, 74, 160)"
   * @example "var(--brand-color)"
   */
  primaryColor: PropTypes.string,
};

InputMaskV2.defaultProps = {
  value: '',
  formatChars: DEFAULT_FORMAT_CHARS,
  size: 'large',
  variant: 'default',
  invalid: false,
  disabled: false,
  autoFocus: false,
  autoComplete: 'off',
  placeholder: undefined,
  name: undefined,
  className: undefined,
  primaryColor: undefined,
};

export default InputMaskV2;

/**
 * Re-exporta constantes úteis para uso externo
 */
export { DEFAULT_FORMAT_CHARS };
